/*
 * SubscriptionSettingsPanel — Slot in /profile?tab=settings.
 *
 * Shows the user's current subscription (tier · billing cycle · status) with:
 *   • Change Subscription  → deep-link to /plans
 *   • Cancel Subscription  → POST /stripe/subscription/cancel
 *
 * Reads from /auth/me. Optimistic UI on cancel.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../utils/auth";
import { toast } from "sonner";
import {
  Crown, Sparkles, Zap, Gem, Loader2, ArrowRightCircle, XCircle,
} from "lucide-react";

const TIER_META = {
  BRONZE: { icon: Zap, color: "text-amber-500" },
  SILVER: { icon: Sparkles, color: "text-slate-300" },
  GOLD: { icon: Crown, color: "text-yellow-400" },
  PLATINUM: { icon: Gem, color: "text-white" },
  DIAMOND: { icon: Gem, color: "text-white" },
  FREE: { icon: Sparkles, color: "text-gray-500" },
};

export default function SubscriptionSettingsPanel({ isCyber = false }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await authAxios.get("/auth/me");
      setSub({
        tier: (r.data?.subscription_tier || "FREE").toUpperCase(),
        cycle: r.data?.subscription_billing_cycle || null,
        status: r.data?.stripe_subscription_status || null,
        periodEnd: r.data?.stripe_current_period_end || null,
        cancelAtEnd: !!r.data?.stripe_subscription_cancel_at_period_end,
      });
    } catch (err) {
      // fail-soft
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await authAxios.post("/stripe/subscription/cancel");
      toast.success("Subscription cancelled — coins already credited stay yours");
      setConfirm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className={`rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass-gold"}`} data-testid="subscription-settings-panel">
        <div className="flex items-center gap-2 text-white/50 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading subscription…
        </div>
      </div>
    );
  }

  const tier = (sub?.tier || "FREE").toUpperCase();
  const meta = TIER_META[tier] || TIER_META.FREE;
  const Icon = meta.icon;
  const isFree = tier === "FREE";
  const cycleLabel = sub?.cycle
    ? sub.cycle.charAt(0).toUpperCase() + sub.cycle.slice(1)
    : null;

  // Charge display — mirrors /app/backend/routes/stripe_payments.py::SUB_TIERS.
  // Rendered inside the plan tile so users see exactly what will next be
  // charged and when.
  const TIER_PRICE = {
    BRONZE:   { monthly: 10, yearly: 110 },
    SILVER:   { monthly: 25, yearly: 250 },
    GOLD:     { monthly: 50, yearly: 450 },
    PLATINUM: { monthly: 75, yearly: 600 },
    DIAMOND:  { monthly: 75, yearly: 600 }, // legacy alias for PLATINUM
  };
  const priceMeta = !isFree ? TIER_PRICE[tier] : null;
  const chargeUsd = priceMeta
    ? sub?.cycle === "yearly" ? priceMeta.yearly : priceMeta.monthly
    : null;
  const chargeLabel = sub?.cycle === "yearly" ? "/ year" : "/ month";
  const renewalDate = sub?.periodEnd
    ? new Date(sub.periodEnd * 1000).toLocaleDateString(undefined, {
        year: "numeric", month: "short", day: "numeric",
      })
    : null;

  return (
    <div className={`rounded-xl p-6 ${isCyber ? "glass-cyber" : "glass-gold"}`} data-testid="subscription-settings-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-white"}`}>Subscription</h3>
        <div className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
          sub?.status === "active" || sub?.status === "trialing"
            ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            : "bg-white/5 text-white/50 border border-white/10"
        }`}>
          {sub?.status || (isFree ? "Free tier" : "—")}
        </div>
      </div>

      <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg mb-4">
        <Icon className={`w-9 h-9 ${meta.color}`} />
        <div className="flex-1 min-w-0">
          <div className={`text-xl font-semibold ${meta.color}`} data-testid="current-plan-tier">{tier}</div>
          <div className="text-white/50 text-sm mt-0.5">
            {isFree
              ? "Upgrade to unlock deeper bonuses & bigger coin packs."
              : `${cycleLabel || "Monthly"} billing`}
          </div>
          {!isFree && chargeUsd !== null && (
            <div className="text-white/70 text-sm mt-1 font-mono" data-testid="subscription-charge-label">
              ${chargeUsd}<span className="text-white/40 ml-1">{chargeLabel}</span>
            </div>
          )}
          {renewalDate && (
            <div className="text-white/50 text-xs mt-1 flex items-center gap-1.5" data-testid="subscription-renewal-row">
              <span>{sub?.cancelAtEnd ? "Ends on" : "Next payment due"}</span>
              <span className="text-white/80 font-mono">{renewalDate}</span>
            </div>
          )}
        </div>
      </div>

      {sub?.cancelAtEnd && !isFree && (
        <div
          className="mb-4 p-2.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-300 text-xs"
          data-testid="subscription-cancel-banner"
        >
          Cancellation scheduled — you keep {tier} benefits until {renewalDate || "the end of this period"}.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <button
          onClick={() => navigate("/plans", { state: { from: "/profile?tab=settings" } })}
          data-testid="change-subscription-btn"
          className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border transition ${
            isCyber
              ? "border-cyan-400/50 text-cyan-400 hover:bg-cyan-400/10"
              : "border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"
          }`}
        >
          <ArrowRightCircle className="w-4 h-4" />
          {isFree ? "Choose a Plan" : "Change Subscription"}
        </button>

        {!isFree && (
          confirm ? (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="cancel-confirm-btn"
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium bg-red-500/90 hover:bg-red-500 text-white transition disabled:opacity-60"
              >
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                {cancelling ? "Cancelling…" : "Confirm Cancel"}
              </button>
              <button
                onClick={() => setConfirm(false)}
                className="px-3 py-2.5 rounded-lg text-sm text-white/60 hover:text-white border border-white/10 transition"
              >
                Back
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirm(true)}
              data-testid="cancel-subscription-btn"
              className="flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium border border-red-500/40 text-red-300 hover:bg-red-500/10 transition"
            >
              <XCircle className="w-4 h-4" />
              Cancel Subscription
            </button>
          )
        )}
      </div>

      {!isFree && (
        <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
          Cancelling stops future renewals — coins already credited stay yours. You&apos;ll drop
          back to FREE tier at the end of the current billing period.
        </p>
      )}
      {isFree && (
        <p className="text-[11px] text-white/40 mt-3 leading-relaxed">
          You&apos;re on the FREE tier. Upgrade to a paid plan for larger monthly coin allowances,
          bigger purchase bonuses, and access to premium coaching depth.
        </p>
      )}
    </div>
  );
}
